import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="home" className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-white -z-10" />
      <div className="absolute top-20 right-10 w-72 h-72 bg-blue-200/20 rounded-full blur-3xl -z-10" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-teal-200/15 rounded-full blur-3xl -z-10" />

      <div className="max-w-7xl mx-auto">
        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left - Text Content */}
          <div className="text-center lg:text-left">
            {/* Main Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 leading-tight mb-6 break-words">
              Building Confident Readers.
              <span className="text-[#3B4BA0] block mt-2">Empowering Confident Educators.</span>
            </h1>

            {/* Sub-headline */}
            <p className="text-lg sm:text-xl text-gray-700 leading-relaxed mb-8">
              Read Right Academy is an early literacy and teacher-training platform
              helping children read with confidence — and preparing parents, teachers,
              and schools with the right foundations to guide them.
            </p>

            {/* Buttons — 2 primary + 1 secondary */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start">
              <Button
                size="lg"
                onClick={() => scrollToSection('programs')}
                data-testid="hero-children-programmes-btn"
                className="bg-[#3B4BA0] hover:bg-[#2d3a7a] text-white text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 shadow-lg hover:shadow-xl transition-all duration-300 group w-full sm:w-auto whitespace-normal h-auto"
              >
                Explore Children&apos;s Programmes
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                onClick={() => scrollToSection('learn-to-teach')}
                data-testid="hero-educator-training-btn"
                className="bg-teal-600 hover:bg-teal-700 text-white text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 shadow-lg hover:shadow-xl transition-all duration-300 group w-full sm:w-auto whitespace-normal h-auto"
              >
                Explore Educator Training
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => scrollToSection('for-schools')}
                data-testid="hero-schools-btn"
                className="border-2 border-[#3B4BA0] text-[#3B4BA0] hover:bg-[#3B4BA0] hover:text-white text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 transition-all duration-300 group w-full sm:w-auto whitespace-normal h-auto"
              >
                For Schools &amp; Institutions
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>

            {/* Quick Links — small jump pills (especially helpful on mobile) */}
            <div
              data-testid="hero-quick-links"
              className="mt-6 flex flex-wrap justify-center lg:justify-start gap-2 text-sm"
            >
              <span className="text-gray-500 mr-1 self-center">Quick links:</span>
              {[
                { label: 'Programmes', id: 'programs' },
                { label: 'How It Works', id: 'how-it-works' },
                { label: 'Articles', id: 'articles' },
                { label: 'FAQ', id: 'faq' },
                { label: 'Contact', id: 'contact' }
              ].map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  data-testid={`quick-link-${link.id}`}
                  className="inline-flex items-center px-3 py-1.5 rounded-full bg-white/70 border border-gray-200 text-gray-700 hover:bg-[#3B4BA0] hover:text-white hover:border-[#3B4BA0] transition-all duration-200 font-medium"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>

          {/* Right - Image Collage */}
          <div className="relative h-[500px] lg:h-[600px] hidden lg:block">
            {/* Main Image - Phonics Learning */}
            <div className="absolute top-0 right-0 w-3/4 h-3/5 rounded-2xl overflow-hidden shadow-2xl transform rotate-2 hover:rotate-0 transition-transform duration-500 z-10">
              <img
                src="https://customer-assets.emergentagent.com/job_early-literacy-hub-3/artifacts/qwiv1gmy_WhatsApp%20Image%202026-05-25%20at%208.50.50%20PM.jpeg"
                alt="Child learning phonics with letter cards"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Second Image - Reading */}
            <div className="absolute bottom-0 left-0 w-3/4 h-3/5 rounded-2xl overflow-hidden shadow-2xl transform -rotate-2 hover:rotate-0 transition-transform duration-500 z-20">
              <img
                src="https://customer-assets.emergentagent.com/job_early-literacy-hub-3/artifacts/gd0nwwi3_axl%20reading.jpg"
                alt="Child reading a book"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Mobile Image - Shown only on mobile */}
          <div className="lg:hidden grid grid-cols-2 gap-4">
            <div className="aspect-square rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_early-literacy-hub-3/artifacts/qwiv1gmy_WhatsApp%20Image%202026-05-25%20at%208.50.50%20PM.jpeg"
                alt="Child learning phonics"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_early-literacy-hub-3/artifacts/gd0nwwi3_axl%20reading.jpg"
                alt="Child reading"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Trust Statement */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#3B4BA0] mb-3">
            Read Right Academy
          </h2>
          <p className="text-lg text-gray-800 leading-relaxed">
            <strong>Founded by Teacher Kalah</strong> — an early literacy specialist,
            Montessori-trained educator, and teacher trainer with over 20 years of
            classroom experience — Read Right Academy helps children build strong
            foundations in sound awareness, phonics, reading, writing readiness,
            vocabulary, comprehension, and learning confidence.
          </p>
        </div>

        {/* Online Classes Message */}
        <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-2xl p-8 mt-8 max-w-4xl mx-auto border-2 border-blue-100">
          <div className="flex items-start sm:items-center gap-4 sm:gap-6 flex-col sm:flex-row text-center sm:text-left">
            <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-[#3B4BA0] to-teal-500 rounded-xl flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-base sm:text-lg text-gray-800 leading-relaxed">
              <strong className="text-[#3B4BA0]">Online classes are available across different time zones</strong> — giving children flexible access to early literacy support wherever they are.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;